<template lang="pug">
FcwMap.FcwEntitiesMap(
  ref="mapRef"
  :controls="['scale', 'geolocate', 'navigation']"
  :center="mapCenter"
  :max-zoom="stationsMapConfig?.maxZoom"
  :min-zoom="stationsMapConfig?.minZoom"
  :initial-zoom="stationsMapConfig?.initialZoom"
  scroll-zoom
  enable-rotation
  :loading="loading || !areStationsReady"
  :height
  :width
  @load="onMapLoad"
)
  .FcwEntitiesMap__centerMapBounds(ref="safeMapAreaRef")
  FcwMapLine.FcwEntities__direction(
    color="primary--light-1"
    dashed
    :auto-fit-bounds="false"
    :coordinates="direction"
    :width="2"
    :layer-id="directionLayerId"
  )
  FcwStationSheet.FcwEntitiesMap__sheet(
    ref="stationSheetRef"
    v-model="isStationSideSheetOpen"
    v-bind="selectedStationSheetProps"
    :loading="isStationDataLoading"
    :walking-time="duration"
    :distance="distance"
    :last-updated-at="stationLastUpdatedAt"
    @open="easeToMapMarker"
    @close="unselectStation"
  )
  FcwPointOfInterestSheet.FcwEntitiesMap__sheet(
    ref="pointOfInterestSheetRef"
    v-model="isPointOfInterestSideSheetOpen"
    v-bind="selectedPointOfInterestSheetProps"
    :loading="isPointOfInterestDataLoading"
    :walking-time="duration"
    :distance="distance"
    :last-updated-at="pointOfInterestLastUpdatedAt"
    @open="easeToMapMarker"
    @close="unselectPointOfInterest"
  )
  FButton.FcwEntitiesMap__legendTrigger(
    icon
    background="neutral--light-5"
    color="neutral--dark-5"
    @click="showLegend = true"
  )
    FIcon(name="infoCircle")
  FcwEntitiesMapLegend.FcwEntitiesMap__legendSheet(
    ref="legendRef"
    v-model="showLegend"
    position="right"
    :station-items
    :point-of-interest-items
    @open="easeToMapMarker"
  )
</template>

<style lang="stylus">
.FcwEntitiesMap__popup
  text-align center
  padding rem(16)

.FcwEntitiesMap__popup__label
  color var(--color--primary)

.FcwEntitiesMap__popup__bikes
  use-font('body-2')

.FcwEntitiesMap__sheet
  position absolute
  z-index 2
  margin-top rem(32)
  height 'calc(100% - %s)' % rem(32 + 16)

  +media-down('md')
    margin-top rem(16)
    height 'calc(100% - %s)' % rem(32)

  +media-down('sm')
    margin-top rem(0)
    height 100%

.FButton.FcwEntitiesMap__legendTrigger
  position absolute
  right rem(16)
  top rem(32)
  z-index 1
  elevation-light(2)

  +media-down('md')
    top rem(16)

.FcwEntitiesMapLegend.FcwEntitiesMap__legendSheet
  position absolute
  z-index 1
  padding-top rem(40)

  .FcwSheet__closeButton
    top rem(40)

  +media-down('sm')
    height 100%

.FcwEntitiesMap__centerMapBounds
  position absolute
  inset rem(48)
  // Uncomment these lines to debug the "safe" entity area
  // background-color: rgba(255, 0, 0, 0.1);
  // border 1px solid rgba(255, 0, 0, 0.3);
  z-index 1
  pointer-events none
</style>

<script setup lang="ts">
import { nanoid } from 'nanoid';
import { FIcon } from '@fifteen/design-system-vue';

import { useStations } from './composables/useStations';
import { usePointsOfInterest } from './composables/usePointsOfInterest';
import { useStationLayers } from './composables/useStationLayers';
import { usePointOfInterestLayers } from './composables/usePointOfInterestLayers';
import { useAreaLayers } from './composables/useAreaLayers';
import { useDirectionLayers } from './composables/useDirectionLayers';
import { useDirections } from './composables/useDirections';
import { useLegendItems } from './composables/useLegendItems';
import { useSafeMapMarkerPosition } from './composables/useSafeMapMarkerPosition';
import { MAP_DATA_REFRESH_INTERVAL } from './config';

import FcwMap from '@/components/molecules/FcwMap.vue';
import { setCursorDefault, setCursorPointer } from '@/lib/helpers/mapbox';

import type { Map } from 'mapbox-gl';
import type FcwPointOfInterestSheet from '../FcwPointOfInterestSheet.vue';
import type FcwStationSheet from '@/components/organisms/FcwStationSheet.vue';
import type FcwEntitiesMapLegend from '@/components/organisms/FcwEntitiesMapLegend';

export interface FcwEntitiesMapProps {
  /**
   * Array of stations
   */
  stations: MapStation[];
  /**
   * Array of points of interest
   */
  pointsOfInterest: MapPointOfInterest[];
  /**
   * Area
   */
  area?: Area;
  /**
   * Loading state of the map
   */
  loading?: boolean;
  /**
   * Height of the map
   */
  height?: string;
  /**
   * Width of the map
   */
  width?: string;
}

const props = withDefaults(defineProps<FcwEntitiesMapProps>(), {
  height: '100%',
  width: '100%',
  area: undefined,
  loading: false,
});

const emit = defineEmits<{
  (name: 'refresh-map'): void;
}>();

const directionLayerId = nanoid();

/**
 * Get map CMS config center, otherwise fallback to runtime config center
 */
const mapCenter = computed(
  () =>
    stationsMapConfig?.center ??
    (useRuntimeConfig().public.mapCenter as Coordinates)
);

const map = ref<Map>();
const mapRef = ref<InstanceType<typeof FcwMap> | null>(null);

function onMapLoad(_map: Ref<Map | undefined>): void {
  map.value = _map.value;
}

useEventListener('keydown', event => {
  if (event.key === 'Escape') {
    unselectAll();
  }
});

const safeMapAreaRef = ref<HTMLElement | null>(null);

const stationSheetRef = ref<InstanceType<typeof FcwStationSheet> | null>(null);
const pointOfInterestSheetRef = ref<InstanceType<
  typeof FcwPointOfInterestSheet
> | null>(null);
const legendRef = ref<InstanceType<typeof FcwEntitiesMapLegend> | null>(null);

async function unselectAll(): Promise<void> {
  await unselectPointOfInterest();
  await unselectStation();
}

async function unselectStation(): Promise<void> {
  await selectStation(null);

  if (selectedEntityType.value === 'station') {
    selectedEntityId.value = null;
    selectedEntityType.value = null;
    clear();
  }
}

async function unselectPointOfInterest(): Promise<void> {
  await selectPointOfInterest(null);

  if (selectedEntityType.value === 'point-of-interest') {
    selectedEntityId.value = null;
    selectedEntityType.value = null;
    clear();
  }
}

const { easeToEntity } = useSafeMapMarkerPosition(mapRef, {
  stationSheetRef,
  pointOfInterestSheetRef,
  legendRef,
  safeMapAreaRef,
});

const selectedEntityId = ref<string | null>();
const selectedEntityType = ref<'station' | 'point-of-interest' | null>();

const isStationSideSheetOpen = computed({
  get: () => selectedEntityType.value === 'station',
  set: (value: boolean) => {
    selectedEntityType.value = value ? 'station' : null;
  },
});
const isPointOfInterestSideSheetOpen = computed({
  get: () => selectedEntityType.value === 'point-of-interest',
  set: (value: boolean) => {
    selectedEntityType.value = value ? 'point-of-interest' : null;
  },
});

const selectedEntityCoordinates = computed<Coordinates | null>(() => {
  switch (selectedEntityType.value) {
    case 'station':
      return selectedStation.value?.coordinates ?? null;
    case 'point-of-interest':
      return selectedPointOfInterest.value?.coordinates ?? null;
    default:
      return null;
  }
});

const { duration, distance, direction } = useDirections(
  selectedEntityCoordinates
);

const {
  selectedStation,
  selectedStationSheetProps,
  selectStation,
  selectedStationId,
  fetchStation,
  isLoading: isStationDataLoading,
  lastUpdatedAt: stationLastUpdatedAt,
  stationsMapConfig,
} = useStations(toRef(() => props.stations));
const { symbolStationsLayerId, isReady: areStationsReady } = useStationLayers(
  map,
  toRef(() => props.stations),
  selectedStationId,
  { fitBounds: mapRef.value?.fitBounds }
);

const {
  selectedPointOfInterest,
  selectedPointOfInterestSheetProps,
  selectPointOfInterest,
  selectedPointOfInterestId,
  fetchPointOfInterest,
  isLoading: isPointOfInterestDataLoading,
  lastUpdatedAt: pointOfInterestLastUpdatedAt,
} = usePointsOfInterest(toRef(() => props.pointsOfInterest));
const { pointsOfInterestLayerId } = usePointOfInterestLayers(
  map,
  toRef(() => props.pointsOfInterest),
  selectedPointOfInterestId
);

useAreaLayers(
  map,
  toRef(() => props.area)
);

const { setLayers, showLayers, hideLayers } = useDirectionLayers(map);

watchImmediate(direction, newDirection => {
  if (newDirection && newDirection.length >= 1) {
    setLayers(newDirection[0], newDirection[newDirection.length - 1]);
    showLayers();
  } else {
    hideLayers();
  }
});

const { pause: clear, resume: start } = useIntervalFn(
  fetchEntities,
  MAP_DATA_REFRESH_INTERVAL,
  {
    immediate: false,
  }
);

async function fetchEntities(): Promise<void> {
  if (
    selectedEntityType.value === 'station' &&
    selectedStationId.value !== null
  ) {
    await fetchStation(selectedStationId.value);
  }
  if (
    selectedEntityType.value === 'point-of-interest' &&
    selectedPointOfInterestId.value !== null
  ) {
    await fetchPointOfInterest(selectedPointOfInterestId.value);
  }
}

function handleEntitiesIntervalFetch(): void {
  emit('refresh-map');

  start();
}

function easeToMapMarker(): void {
  easeToEntity(selectedEntityCoordinates);
}

async function selectEntity(
  feature: mapboxgl.MapboxGeoJSONFeature
): Promise<void> {
  if (!feature) return;

  const isAlreadySelectedEntity = Boolean(selectedEntityId.value);

  const { id, entityType } = feature.properties as {
    id: string;
    entityType: string;
  };

  switch (entityType) {
    case 'station': {
      await selectStation(id);
      await selectPointOfInterest(null);

      selectedEntityId.value = id;
      selectedEntityType.value = 'station';
      break;
    }
    case 'point-of-interest': {
      await selectStation(null);
      await selectPointOfInterest(id);

      selectedEntityId.value = id;
      selectedEntityType.value = 'point-of-interest';
      break;
    }
  }

  // If no entity was previously selected, we prevent the entity selection ease from here, to let the entity sheet perform the ease
  // We do this to avoid an "ease to" from the entity while the station sheet is not opened yet
  if (isAlreadySelectedEntity) {
    await easeToMapMarker();
  }
}

onMounted(async () => {
  await until(map).not.toBeUndefined();

  const layers = [symbolStationsLayerId, pointsOfInterestLayerId];

  // Remove existing event listeners
  map.value?.off('mouseenter', layers, () => setCursorPointer(map));
  map.value?.off('mouseleave', layers, () => setCursorDefault(map));
  map.value?.off('click', layers, event => {
    const feature = event.features?.[0];

    event.preventDefault();
    if (feature) {
      selectEntity(feature);
    }
  });
  map.value?.off('click', event => {
    if (!event.defaultPrevented) {
      unselectAll();
    }
  });

  // Add event listeners
  map.value?.on('mouseenter', layers, () => setCursorPointer(map));
  map.value?.on('mouseleave', layers, () => setCursorDefault(map));

  // Prevent double click from unselecting the entity
  let isDoubleClick = false;

  map.value?.on('click', layers, async event => {
    event.preventDefault();
    const feature = event.features?.[0];

    if (feature) {
      selectEntity(feature);
      handleEntitiesIntervalFetch();
    }
  });

  map.value?.on('click', async event => {
    isDoubleClick = false;

    if (event.defaultPrevented) return;

    setTimeout(() => {
      if (!isDoubleClick) {
        unselectAll();
      }
    }, 250);
  });

  map.value?.on('dblclick', () => {
    isDoubleClick = true;
  });
});

const showLegend = ref(false);

const { stationItems, pointOfInterestItems } = useLegendItems({
  stations: toRef(() => props.stations),
  pointsOfInterest: toRef(() => props.pointsOfInterest),
});
</script>
